import React from "react";
import { useTranslation } from "react-i18next";
import {
  Brain,
  HeartPulse,
  CalendarClock,
  Trophy,
  Users,
  Stethoscope,
  CloudSun,
  School,
  Baby,
  ArrowUpRight,
} from "lucide-react";

// Order + meta. Translations come from i18n key features.items.{key}
const FEATURE_CONFIG = [
  { key: "equicoach", icon: Brain, tone: "primary", span: "lg:col-span-2 lg:row-span-2", video: "/assets/equicoach.mp4", hasLabel: true },
  { key: "paardenmanagement", icon: HeartPulse, tone: "soft" },
  { key: "planning", icon: CalendarClock, tone: "soft" },
  { key: "wedstrijd", icon: Trophy, tone: "accent" },
  { key: "samenwerken", icon: Users, tone: "soft" },
  { key: "professionals", icon: Stethoscope, tone: "soft" },
  { key: "indexen", icon: CloudSun, tone: "soft" },
  { key: "manege", icon: School, tone: "soft" },
  { key: "fokkerij", icon: Baby, tone: "accent" },
];

const cardTone = (tone) => {
  switch (tone) {
    case "primary":
      return "bg-[hsl(var(--forest))] text-[hsl(var(--background))] border-transparent";
    case "accent":
      return "bg-[hsl(var(--sand))]/15 border-[hsl(var(--sand))]/30";
    default:
      return "bg-[hsl(var(--surface))] border-[hsl(var(--border))]";
  }
};

export const Features = () => {
  const { t } = useTranslation();

  return (
    <section
      id="features"
      data-testid="features-section"
      className="bg-[hsl(var(--surface-secondary))] py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
              {t("features.eyebrow")}
            </p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-5xl lg:text-6xl">
              {t("features.title")}
            </h2>
            <p className="mt-6 max-w-md text-[hsl(var(--ink-soft))]">
              {t("features.intro")}
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="grid auto-rows-[minmax(180px,auto)] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURE_CONFIG.map(({ key, icon: Icon, tone, span, video, hasLabel }) => {
                const title = t(`features.items.${key}.title`);
                const text = t(`features.items.${key}.text`);
                const label = hasLabel ? t(`features.items.${key}.label`) : null;
                return (
                  <article
                    key={key}
                    data-testid={`feature-${key}`}
                    className={`group relative flex flex-col justify-between rounded-3xl border p-7 transition-all hover:-translate-y-1 ${cardTone(
                      tone
                    )} ${span ?? ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`grid h-10 w-10 place-items-center rounded-full ${
                          tone === "primary"
                            ? "bg-[hsl(var(--background))]/10 text-[hsl(var(--background))]"
                            : "bg-[hsl(var(--forest))]/8 text-[hsl(var(--forest))]"
                        }`}
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.5} />
                      </span>
                      {label && (
                        <span
                          className={`text-[10px] uppercase tracking-[0.22em] ${
                            tone === "primary"
                              ? "text-[hsl(var(--sand))]"
                              : "text-[hsl(var(--ink-soft))]"
                          }`}
                        >
                          {label}
                        </span>
                      )}
                      {!label && (
                        <ArrowUpRight
                          className={`h-4 w-4 opacity-0 transition-all group-hover:opacity-60 ${
                            tone === "primary"
                              ? "text-[hsl(var(--background))]"
                              : "text-[hsl(var(--ink))]"
                          }`}
                          strokeWidth={1.5}
                        />
                      )}
                    </div>

                    <div className={video ? "mt-6" : "mt-10"}>
                      {video && (
                        <div
                          data-testid={`feature-video-${key}`}
                          className="mb-6 flex justify-center"
                        >
                          <div className="relative overflow-hidden rounded-[2rem] border border-[hsl(var(--background))]/15 bg-black shadow-[0_25px_60px_-30px_rgba(0,0,0,0.55)]" style={{ maxWidth: "220px", aspectRatio: "9 / 19" }}>
                            <video
                              src={video}
                              autoPlay
                              loop
                              muted
                              playsInline
                              preload="metadata"
                              className="block h-full w-full object-cover"
                            />
                          </div>
                        </div>
                      )}
                      <h3
                        className={`font-display text-2xl font-medium leading-tight ${
                          tone === "primary"
                            ? "text-[hsl(var(--background))]"
                            : "text-[hsl(var(--ink))]"
                        }`}
                      >
                        {title}
                      </h3>
                      <p
                        className={`mt-2 text-sm leading-relaxed ${
                          tone === "primary"
                            ? "text-[hsl(var(--background))]/75"
                            : "text-[hsl(var(--ink-soft))]"
                        }`}
                      >
                        {text}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
